import {
  CALCULATION_MODES,
  blendHistoryInputSchema,
  imageCategorySchema,
  strainInputSchema,
  type BlendHistoryEntry,
  type Strain,
} from '@shared';
import {
  JOURNAL_STORAGE_KEY,
  isJournalEntry,
  normalizeJournalEntry,
  normalizeRitualAllocation,
  readJournalEntries,
  type JournalEntry,
} from './journalStore';
import type { LocalProductImageRecord } from './productImages/types';
import {
  LOCAL_BLEND_HISTORY_STORE,
  LOCAL_PRODUCT_IMAGES_STORE,
  LOCAL_PRODUCTS_STORE,
  openLocalProductsDb,
  requestToPromise,
  txDone,
  type IdbFactory,
} from './productRepository/localProductsDb';
import {
  USER_PROFILE_STORAGE_KEY,
  isUserProfile,
  type ProfileStorage,
  type UserProfile,
} from './userProfile';

export const PORTABLE_BACKUP_TYPE = 'conscious-ritual-backup';
export const PORTABLE_BACKUP_FORMAT_VERSION = 1;
export const PORTABLE_BACKUP_FILE_EXTENSION = '.cr-backup.json';

interface EncodedProductImage
  extends Omit<LocalProductImageRecord, 'blob'> {
  dataBase64: string;
}

export interface PortableBackupV1 {
  type: typeof PORTABLE_BACKUP_TYPE;
  formatVersion: typeof PORTABLE_BACKUP_FORMAT_VERSION;
  createdAt: string;
  data: {
    profile: UserProfile | null;
    products: Strain[];
    productImages: EncodedProductImage[];
    blendHistory: BlendHistoryEntry[];
    journal: JournalEntry[];
    /** No preference is currently durable; reserved for compatible v1 growth. */
    preferences: Record<string, never>;
  };
}

export interface ValidatedPortableBackup {
  metadata: Pick<PortableBackupV1, 'type' | 'formatVersion' | 'createdAt'>;
  profile: UserProfile | null;
  products: Strain[];
  productImages: LocalProductImageRecord[];
  blendHistory: BlendHistoryEntry[];
  journal: JournalEntry[];
  preferences: Record<string, never>;
}

export interface BackupSummary {
  createdAt: string;
  productCount: number;
  imageCount: number;
  blendHistoryCount: number;
  journalCount: number;
  hasProfile: boolean;
}

export class PortableBackupError extends Error {}

interface DurableSnapshot {
  profile: UserProfile | null;
  products: Strain[];
  productImages: LocalProductImageRecord[];
  blendHistory: BlendHistoryEntry[];
  journal: JournalEntry[];
}

function browserStorage(): ProfileStorage {
  return {
    getItem: (key) => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value),
    removeItem: (key) => window.localStorage.removeItem(key),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

function requiredString(value: unknown, label: string): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new PortableBackupError(`${label} is invalid.`);
  }
  return value;
}

function uniqueIds(rows: Array<{ id: string }>, label: string): void {
  const ids = new Set<string>();
  for (const row of rows) {
    if (ids.has(row.id)) {
      throw new PortableBackupError(`${label} contains a duplicate ID.`);
    }
    ids.add(row.id);
  }
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

function validateProduct(value: unknown): Strain {
  if (!isRecord(value)) throw new PortableBackupError('A product is invalid.');
  requiredString(value.id, 'Product ID');
  if (!isIsoDate(value.createdAt) || !isIsoDate(value.updatedAt)) {
    throw new PortableBackupError('A product timestamp is invalid.');
  }
  if (
    typeof value.isArchived !== 'boolean' ||
    typeof value.isVerified !== 'boolean' ||
    typeof value.isBalancingStrain !== 'boolean' ||
    (value.saveStatus !== 'complete' && value.saveStatus !== 'incomplete') ||
    (value.productId !== null && typeof value.productId !== 'string') ||
    (value.lastSyncedAt !== null && !isIsoDate(value.lastSyncedAt))
  ) {
    throw new PortableBackupError('A product state is invalid.');
  }
  const parsed = strainInputSchema.safeParse({
    name: value.name ?? undefined,
    productName: value.productName ?? undefined,
    producer: value.producer ?? undefined,
    brand: value.brand ?? undefined,
    category: value.category ?? undefined,
    cultivarType: value.cultivarType ?? undefined,
    strainClassSourceText: value.strainClassSourceText ?? undefined,
    lineageSourceText: value.lineageSourceText ?? undefined,
    thcPercent: value.thcPercent,
    cbdPercent: value.cbdPercent ?? undefined,
    cbnPercent: value.cbnPercent ?? undefined,
    cbgPercent: value.cbgPercent ?? undefined,
    thcvPercent: value.thcvPercent ?? undefined,
    cbcPercent: value.cbcPercent ?? undefined,
    totalTerpenesPercent: value.totalTerpenesPercent ?? undefined,
    packageDate: value.packageDate ?? undefined,
    purchaseDate: value.purchaseDate ?? undefined,
    lotNumber: value.lotNumber ?? undefined,
    sourceType: value.sourceType,
    notes: value.notes ?? undefined,
    referenceNotes: value.referenceNotes ?? undefined,
    isVerified: value.isVerified,
  });
  if (!parsed.success) throw new PortableBackupError('A product record is invalid.');
  return { ...(value as unknown as Strain), images: [], primaryImage: null };
}

function validateBlendHistory(value: unknown): BlendHistoryEntry {
  if (!isRecord(value)) {
    throw new PortableBackupError('A Blend History record is invalid.');
  }
  requiredString(value.id, 'Blend History ID');
  if (!isIsoDate(value.createdAt)) {
    throw new PortableBackupError('A Blend History timestamp is invalid.');
  }
  const parsed = blendHistoryInputSchema.safeParse({
    request: value.request,
    note: value.note ?? undefined,
  });
  const result = value.result;
  const reachability =
    isRecord(result) && isRecord(result.reachability)
      ? result.reachability
      : null;
  if (
    !parsed.success ||
    !isRecord(result) ||
    !Array.isArray(result.allocations) ||
    !isFiniteNumber(result.totalWeightGrams) ||
    !Number.isInteger(result.totalUnits) ||
    !isFiniteNumber(result.scalePrecisionGrams) ||
    !Number.isInteger(result.displayDecimals) ||
    !isFiniteNumber(result.estimatedThcMg) ||
    !isFiniteNumber(result.targetThcMg) ||
    !isFiniteNumber(result.differenceMg) ||
    !isFiniteNumber(result.absoluteDifferenceMg) ||
    (result.percentDifference !== null && !isFiniteNumber(result.percentDifference)) ||
    !CALCULATION_MODES.includes(result.mode as (typeof CALCULATION_MODES)[number]) ||
    typeof result.exactlyAchievable !== 'boolean' ||
    !reachability ||
    !isFiniteNumber(reachability.minThcMg) ||
    !isFiniteNumber(reachability.maxThcMg) ||
    !Number.isInteger(reachability.minStrainIndex) ||
    !Number.isInteger(reachability.maxStrainIndex) ||
    !result.allocations.every(
      (allocation) =>
        isRecord(allocation) &&
        Number.isInteger(allocation.strainIndex) &&
        isOptionalString(allocation.strainId) &&
        typeof allocation.name === 'string' &&
        allocation.name.length > 0 &&
        isOptionalString(allocation.producer) &&
        isFiniteNumber(allocation.thcPercent) &&
        Number.isInteger(allocation.units) &&
        isFiniteNumber(allocation.grams) &&
        isFiniteNumber(allocation.thcMg),
    )
  ) {
    throw new PortableBackupError('A Blend History record is invalid.');
  }
  return value as unknown as BlendHistoryEntry;
}

function validateJournal(value: unknown): JournalEntry {
  if (value && typeof value === 'object' && 'allocations' in value) {
    const allocations = (value as { allocations?: unknown }).allocations;
    if (allocations !== undefined && (!Array.isArray(allocations) || allocations.some((allocation) => !normalizeRitualAllocation(allocation)))) {
      throw new PortableBackupError('A Ritual allocation is invalid.');
    }
  }
  const entry = normalizeJournalEntry(value);
  if (!entry) {
    throw new PortableBackupError('A Ritual record is invalid.');
  }
  if (
    !isIsoDate(entry.createdAt) ||
    !isIsoDate(entry.updatedAt) ||
    !isIsoDate(entry.occurredAt)
  ) {
    throw new PortableBackupError('A Ritual record is incomplete.');
  }
  return entry;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

function base64ToBytes(value: unknown): Uint8Array {
  if (typeof value !== 'string' || !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(value)) {
    throw new PortableBackupError('An image payload is invalid.');
  }
  try {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
  } catch {
    throw new PortableBackupError('An image payload is invalid.');
  }
}

function validateImage(value: unknown): LocalProductImageRecord {
  if (!isRecord(value)) throw new PortableBackupError('An image record is invalid.');
  const id = requiredString(value.id, 'Image ID');
  const productId = requiredString(value.productId, 'Image product ID');
  const mimeType = requiredString(value.mimeType, 'Image MIME type');
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(mimeType)) {
    throw new PortableBackupError('An image MIME type is unsupported.');
  }
  if (
    !imageCategorySchema.safeParse(value.imageCategory).success ||
    !isIsoDate(value.createdAt) ||
    typeof value.sortOrder !== 'number' ||
    !Number.isInteger(value.sortOrder) ||
    typeof value.isPrimary !== 'boolean' ||
    (value.originalFilename !== null && typeof value.originalFilename !== 'string') ||
    (value.width !== null && (typeof value.width !== 'number' || value.width <= 0)) ||
    (value.height !== null && (typeof value.height !== 'number' || value.height <= 0))
  ) {
    throw new PortableBackupError('An image record is invalid.');
  }
  const bytes = base64ToBytes(value.dataBase64);
  if (bytes.length === 0) throw new PortableBackupError('An image payload is empty.');
  return {
    id,
    productId,
    blob: new Blob([bytes], { type: mimeType }),
    mimeType,
    originalFilename: value.originalFilename,
    createdAt: value.createdAt,
    sortOrder: value.sortOrder,
    isPrimary: value.isPrimary,
    imageCategory: value.imageCategory as LocalProductImageRecord['imageCategory'],
    width: value.width,
    height: value.height,
  };
}

async function readIndexedDb(factory?: IdbFactory): Promise<Pick<
  DurableSnapshot,
  'products' | 'productImages' | 'blendHistory'
>> {
  const db = await openLocalProductsDb(factory);
  try {
    const tx = db.transaction(
      [LOCAL_PRODUCTS_STORE, LOCAL_PRODUCT_IMAGES_STORE, LOCAL_BLEND_HISTORY_STORE],
      'readonly',
    );
    const [products, productImages, blendHistory] = await Promise.all([
      requestToPromise(tx.objectStore(LOCAL_PRODUCTS_STORE).getAll()),
      requestToPromise(tx.objectStore(LOCAL_PRODUCT_IMAGES_STORE).getAll()),
      requestToPromise(tx.objectStore(LOCAL_BLEND_HISTORY_STORE).getAll()),
    ]);
    await txDone(tx);
    return {
      products: products as Strain[],
      productImages: productImages as LocalProductImageRecord[],
      blendHistory: blendHistory as BlendHistoryEntry[],
    };
  } finally {
    db.close();
  }
}

function readLocalStorage(storage: ProfileStorage): Pick<
  DurableSnapshot,
  'profile' | 'journal'
> {
  const profileRaw = storage.getItem(USER_PROFILE_STORAGE_KEY);
  const journalRaw = storage.getItem(JOURNAL_STORAGE_KEY);
  const profile: unknown = profileRaw ? JSON.parse(profileRaw) : null;
  const journal: unknown = journalRaw ? JSON.parse(journalRaw) : [];
  if (profile !== null && !isUserProfile(profile)) {
    throw new PortableBackupError('The local profile could not be exported.');
  }
  if (!Array.isArray(journal) || !journal.every(isJournalEntry)) {
    throw new PortableBackupError('The local Rituals could not be exported.');
  }
  return { profile, journal: readJournalEntries(journalRaw) };
}

export async function readDurableSnapshot(options: {
  indexedDB?: IdbFactory;
  storage?: ProfileStorage;
} = {}): Promise<DurableSnapshot> {
  const [idb, local] = await Promise.all([
    readIndexedDb(options.indexedDB),
    Promise.resolve(readLocalStorage(options.storage ?? browserStorage())),
  ]);
  return { ...idb, ...local };
}

export async function createPortableBackup(options: {
  indexedDB?: IdbFactory;
  storage?: ProfileStorage;
  now?: () => Date;
} = {}): Promise<PortableBackupV1> {
  const snapshot = await readDurableSnapshot(options);
  const productImages = await Promise.all(
    snapshot.productImages.map(async ({ blob, ...metadata }) => ({
      ...metadata,
      dataBase64: bytesToBase64(new Uint8Array(await blob.arrayBuffer())),
    })),
  );
  const backup: PortableBackupV1 = {
    type: PORTABLE_BACKUP_TYPE,
    formatVersion: PORTABLE_BACKUP_FORMAT_VERSION,
    createdAt: (options.now ?? (() => new Date()))().toISOString(),
    data: {
      profile: snapshot.profile,
      products: snapshot.products,
      productImages,
      blendHistory: snapshot.blendHistory,
      journal: snapshot.journal,
      preferences: {},
    },
  };
  validatePortableBackup(backup);
  return backup;
}

export function serializePortableBackup(backup: PortableBackupV1): string {
  return JSON.stringify(backup);
}

export function portableBackupFilename(createdAt: string): string {
  const stamp = createdAt.replace(/[:.]/g, '-');
  return `Conscious-Ritual-backup-${stamp}${PORTABLE_BACKUP_FILE_EXTENSION}`;
}

export function validatePortableBackup(value: unknown): ValidatedPortableBackup {
  if (!isRecord(value)) throw new PortableBackupError('This is not a Conscious Ritual backup.');
  if (value.type !== PORTABLE_BACKUP_TYPE) {
    throw new PortableBackupError('This is not a Conscious Ritual backup.');
  }
  if (value.formatVersion !== PORTABLE_BACKUP_FORMAT_VERSION) {
    throw new PortableBackupError('This backup version is not supported.');
  }
  if (!isIsoDate(value.createdAt) || !isRecord(value.data)) {
    throw new PortableBackupError('The backup metadata is invalid.');
  }
  const data = value.data;
  if (
    !Array.isArray(data.products) ||
    !Array.isArray(data.productImages) ||
    !Array.isArray(data.blendHistory) ||
    !Array.isArray(data.journal) ||
    !isRecord(data.preferences)
  ) {
    throw new PortableBackupError('The backup data is incomplete.');
  }
  if (Object.keys(data.preferences).length > 0) {
    throw new PortableBackupError('The backup contains unsupported preferences.');
  }
  if (data.profile !== null && !isUserProfile(data.profile)) {
    throw new PortableBackupError('The backup profile is invalid.');
  }
  if (
    data.profile !== null &&
    (!isIsoDate(data.profile.createdAt) || !isIsoDate(data.profile.updatedAt))
  ) {
    throw new PortableBackupError('The backup profile timestamps are invalid.');
  }
  const products = data.products.map(validateProduct);
  const productImages = data.productImages.map(validateImage);
  const blendHistory = data.blendHistory.map(validateBlendHistory);
  const journal = data.journal.map(validateJournal);
  uniqueIds(products, 'Products');
  uniqueIds(productImages, 'Images');
  uniqueIds(blendHistory, 'Blend History');
  uniqueIds(journal, 'Rituals');
  const productIds = new Set(products.map((product) => product.id));
  const primaryCounts = new Map<string, number>();
  for (const image of productImages) {
    if (!productIds.has(image.productId)) {
      throw new PortableBackupError('An image refers to a missing product.');
    }
    if (image.isPrimary) {
      primaryCounts.set(image.productId, (primaryCounts.get(image.productId) ?? 0) + 1);
    }
  }
  const imageProductIds = new Set(productImages.map((image) => image.productId));
  if ([...imageProductIds].some((id) => primaryCounts.get(id) !== 1)) {
    throw new PortableBackupError('Every product with images must have one Primary image.');
  }
  return {
    metadata: {
      type: PORTABLE_BACKUP_TYPE,
      formatVersion: PORTABLE_BACKUP_FORMAT_VERSION,
      createdAt: value.createdAt,
    },
    profile: data.profile,
    products,
    productImages,
    blendHistory,
    journal,
    preferences: {},
  };
}

export function parsePortableBackup(text: string): ValidatedPortableBackup {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new PortableBackupError('The selected file is not valid JSON.');
  }
  return validatePortableBackup(parsed);
}

async function replaceIndexedDb(
  data: Pick<ValidatedPortableBackup, 'products' | 'productImages' | 'blendHistory'>,
  factory?: IdbFactory,
): Promise<void> {
  const db = await openLocalProductsDb(factory);
  try {
    const names = [
      LOCAL_PRODUCTS_STORE,
      LOCAL_PRODUCT_IMAGES_STORE,
      LOCAL_BLEND_HISTORY_STORE,
    ];
    const tx = db.transaction(names, 'readwrite');
    const products = tx.objectStore(LOCAL_PRODUCTS_STORE);
    const images = tx.objectStore(LOCAL_PRODUCT_IMAGES_STORE);
    const history = tx.objectStore(LOCAL_BLEND_HISTORY_STORE);
    const [existingProducts, existingImages, existingHistory] = await Promise.all([
      requestToPromise(products.getAll()),
      requestToPromise(images.getAll()),
      requestToPromise(history.getAll()),
    ]);
    for (const row of existingProducts as Array<{ id: string }>) products.delete(row.id);
    for (const row of existingImages as Array<{ id: string }>) images.delete(row.id);
    for (const row of existingHistory as Array<{ id: string }>) history.delete(row.id);
    for (const row of data.products) products.put({ ...row, images: [], primaryImage: null });
    for (const row of data.productImages) images.put(row);
    for (const row of data.blendHistory) history.put(row);
    await txDone(tx);
  } finally {
    db.close();
  }
}

function replaceLocalStorage(
  data: Pick<ValidatedPortableBackup, 'profile' | 'journal'>,
  storage: ProfileStorage,
): void {
  if (data.profile) {
    storage.setItem(USER_PROFILE_STORAGE_KEY, JSON.stringify(data.profile));
  } else {
    storage.removeItem(USER_PROFILE_STORAGE_KEY);
  }
  storage.setItem(JOURNAL_STORAGE_KEY, JSON.stringify(data.journal));
}

export async function restorePortableBackup(
  data: ValidatedPortableBackup,
  options: { indexedDB?: IdbFactory; storage?: ProfileStorage } = {},
): Promise<void> {
  const storage = options.storage ?? browserStorage();
  const previous = await readDurableSnapshot({
    indexedDB: options.indexedDB,
    storage,
  });
  try {
    await replaceIndexedDb(data, options.indexedDB);
    replaceLocalStorage(data, storage);
  } catch (error) {
    try {
      await replaceIndexedDb(previous, options.indexedDB);
      replaceLocalStorage(previous, storage);
    } catch {
      throw new PortableBackupError(
        'Restore failed and the previous data could not be fully recovered.',
      );
    }
    throw new PortableBackupError(
      error instanceof Error ? `Restore failed: ${error.message}` : 'Restore failed.',
    );
  }
}

export function summarizePortableBackup(data: ValidatedPortableBackup): BackupSummary {
  return {
    createdAt: data.metadata.createdAt,
    productCount: data.products.length,
    imageCount: data.productImages.length,
    blendHistoryCount: data.blendHistory.length,
    journalCount: data.journal.length,
    hasProfile: data.profile !== null,
  };
}
